import React from "react";
import { Button, Callout, Card, Logo } from "../components/DesignSystem";
import type { PublicCollaboratorInvite } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { AuthScreens } from "./AuthScreens";
import { roleLabels } from "./CollaboratorsScreen";
import { LoadingState } from "./common";

export function CollaboratorInviteScreen({ token }: { token: string }) {
  const { service, session, refresh } = usePlannerData();
  const [invite, setInvite] = React.useState<PublicCollaboratorInvite | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [accepting, setAccepting] = React.useState(false);
  const [accepted, setAccepted] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setInvite(await service.collaborators.getPublicInvite(token));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [service, token]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const accept = async () => {
    setAccepting(true);
    setError(null);
    try {
      await service.collaborators.acceptInvite(token);
      await refresh();
      setAccepted(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setAccepting(false);
    }
  };

  const emailMatches = session && invite && session.user.email.toLowerCase() === invite.email.toLowerCase();

  return (
    <div className="pob-app" style={{ background: "var(--surface-page)", overflowY: "auto" }}>
      <div className="pob-pad" style={{ maxWidth: 520, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-6)" }}>
          <Logo height={28} />
          <span style={{ font: "var(--type-label)" }}>Pockets of Beige</span>
        </div>
        {loading && <LoadingState label="Opening invite" />}
        {error && <Callout tone="error" title="This invite link could not be opened" action={<Button size="sm" onClick={load}>Retry</Button>}>{error}</Callout>}
        {!loading && !error && invite && !accepted && (
          <Card padding="var(--space-6)">
            <span className="pob-eyebrow">Collaborator invite</span>
            <h1 style={{ font: "var(--type-section)", margin: "8px 0 4px" }}>Join {invite.weddingTitle}</h1>
            <p style={{ font: "var(--type-body)", color: "var(--text-secondary)", marginBottom: "var(--space-5)" }}>
              You've been invited as <strong>{roleLabels[invite.role]}</strong>. This invite was sent to {invite.email}.
            </p>
            {!session && <AuthScreens onAuthenticated={() => {}} />}
            {session && !emailMatches && (
              <Callout tone="warning" title="Signed in with a different email">
                You're signed in as {session.user.email}, but this invite was sent to {invite.email}. Sign out and sign in with the invited email to accept.
              </Callout>
            )}
            {session && emailMatches && (
              <Button variant="primary" disabled={accepting} onClick={accept}>{accepting ? "Joining…" : `Accept and join as ${roleLabels[invite.role]}`}</Button>
            )}
          </Card>
        )}
        {accepted && invite && (
          <Card padding="var(--space-6)">
            <Callout tone="success" title="You're in">You've joined {invite.weddingTitle} as {roleLabels[invite.role]}.</Callout>
            <div style={{ marginTop: "var(--space-4)" }}>
              <Button variant="primary" onClick={() => { window.location.href = "/"; }}>Go to the planner</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
